/**
 * TTL-Constants
 *
 * Description:
 * Defines common constants
 */

/*
 * Gender Enumeration
 */
const  Gender = Object.freeze({
    Male: 0,
    Female: 1
});

const Permissions = Object.freeze({

    User: {
        ReadNotifications: "perm_read_notifications",
        MonitorTrackers: "perm_monitor_trackers",
        ChangePassword: "perm_change_password",
        ChangeEmail: "perm_change_email",
        ChangePhone: "perm_change_phone"
    },

    Requests: {
        View: "perm_read_request",
        Assign: "perm_assign_request",
        Cancel: "perm_cancel_request",
        Complete: "perm_complete_request",
        Create: "perm_create_request",
        Remove: "perm_delete_request",
        Update: "perm_update_request"
    },

    Tractors: {
        Create: "perm_add_tractor",
        Update: "perm_edit_tractor",
        View: "perm_read_tractor"
    },

    Operators: {
        Create: "perm_add_operators",
        Update: "perm_update_operators",
        View: "perm_read_operators",
        Remove: "perm_delete_operators"
    },

    TractorOwners: {
        Create: "perm_register_owner",
        Update: "perm_update_owners",
        View: "perm_read_owners"
    },

    Farmers: {
        Create: "perm_add_farmer",
        Update: "perm_edit_farmer",
        View: "perm_read_farmer",
        Remove: "perm_delete_farmer"
    },

    FBO: {
        Create: "perm_register_fbo",
        Update: "perm_edit_fbo",
        View: "perm_read_fbo",
        Remove: "perm_delete_fbo"
    },

    Admin: {
        Create: "perm_register_admin"
    },

    USSDServices: {
        Create: "perm_add_ussd_services",
        Update: "perm_edit_ussd_services",
        View: "perm_read_ussd_services"
    },

    Trackers: {
        View: "perm_read_tracker",
        Install: "perm_add_tracker",
        Remove: "perm_delete_tracker",
        Edit: "perm_edit_tracker",
    },

    Transactions: {
        View: "perm_read_transactions"
    },

    Payments: {
        Issue: "perm_issue_payment",
        View: "perm_read_payments"
    },

    CurrencyConversion: {
        View: "perm_read_currency_rates",
        Update: "perm_edit_currency_rates"
    },

    Wallet: {
        Manage: "perm_manage_wallet"
    },

    CommandAgriculture: {
        Create: "perm_add_command_agriculture_payment",
        View: "perm_read_command_agriculture_payment",
        Edit: "perm_edit_command_agriculture_payment",
    }

});

/*
 * Entity Source Enumeration
 */
const SourceEnum = Object.freeze({
    TTL: 0
});

const GPSDeviceStatus = Object.freeze({
    NotActivated: 0,
    Running : 1,
    Online: 2,
    Expired: 4,
    Offline: 3
});

/**
 * Contains Known GPS Commands
 * @type {Object}
 */
const KnownDeviceCommands = Object.freeze({

    /**
     * CutOff Engine
     */
    CutOffEngine: "cutoff-engine",

    /**
     * Resume Engine
     */
    ResumeEngine: "resume-engine"

});

const UserDepartments = Object.freeze({

    /**
     * The finance department
     */
    Finance: 'finance',

    /**
     * The Information Technology department
     */
    IT: 'IT',

    /**
     * Grounds & Operations department
     */
    Operations: 'operations',

    /**
     * Human Resource department
     */
    HumanResource: 'HR'

});

/*
 * Owner Account Type Enumeration
 */
const OwnerAccountType = Object.freeze({
    Individual: 0,
    Company: 1
});

/*
 * Media Category Enumeration
 */
const MediaCategory = Object.freeze({
    Profile: 0
});

/*
 * Tractor Request Type Enumeration
 */
const TractorRequestType = Object.freeze({
    Plough: 0,
    Planting: 1,
    Harrow: 2,
    Shelling: 3,
    Ridging: 4,
    Harvesting: 5
});

const UserActivity = Object.freeze({
    Online: 'online',
    Offline: 'offline'
});

const VerifyStatus = Object.freeze({
    Passed: "passed",
    Failed: "failed"
});

/**
 * Tractor Suggestion Actions
 * @type {Object}
 */
const TractorSuggestActions  = Object.freeze({
   Assignment: "assign"
});

const PaymentChangeType = Object.freeze({

    /**
     * Increment
     */
    Increment: 1,

    /**
     * Decrement
     */
    Decrement: -1

});

/**
 * Represents valid status for responses
 */
const ResponseStatus = Object.freeze({

        //  Represents a successful status
        Succeed: 0,

        //  Represents an internal sever error
        ServerError: -2,

        //  A validation error occurred which prevented the operation
        ValidationError: -3,

        //  Not found error. A requested resource was not found
        NotFound: -4,

        //  Represents a failed operation
        Fail: -1
});


/*
 * Tractor Request Binder (Assigner)
 */
const TractorRequestBinder = Object.freeze({

    //  Automatic assignment
    CPU: 0,

    //  Administrator
    Admin: 1
});

/**
 * Defines supported device platforms
 * @type {Object}
 */
const DevicePlatforms = Object.freeze({
   Desktop: 'desktop',
   Mobile: 'mobile'
});

/*
 * Tractor Request Status Enumeration
 */
const RequestStatus = Object.freeze({
    Completed: 0,
    Assigned: 1,
    Unassigned: 2,
    Cancelled: 3
});

const TractorOwnerRequestPaymentStatus = Object.freeze({
    Success: 0,
    Failed: -1,
    Cancelled: -2
});

/*
 * Tractor State Enumeration
 */
const TractorState = Object.freeze({
    Active: 0,
    Faulted: 1
});


/*
 * Message delivery threshold
 */
const DeliveryThreshold = Object.freeze({
    Immediate: 0,
    Daily: 1,
    Weekly: 2,
    Monthly: 3,
    Quarterly: 4,
    Yearly: 5
});


/*
 * Media Storage Enumeration
 */
const MediaStorage = Object.freeze({
    Disk: 0,
    CDN: 1
});

/*
 * Defines User Roles
 */
const UserRoles = Object.freeze({

    /*
     * The is the major overseer of all operations. Has access to any resource on the server.
     */
    Admin: 'admin',

    /*
     * This role is used by tractor owners.
     */
    Owner: 'owner',

    /*
     * This role is used by tractor owners.
     */
    FBO: 'fbo',

    /**
     * This role is used by call center agents
     */
    CallCenterAgent: 'call_center_agent',

    /**
     * This role is used by farmers
     */
    Farmer: 'farmer'
});

const TicketPriority = Object.freeze({

    /**
     * High priority
     */
    High: 2,

    /**
     * Medium priority
     */
    Medium: 1,

    /**
     * Low priority
     */
    Low: 0

});

const TicketStatus = Object.freeze({

    /**
     * The ticket has just been sent
     */
    Idle: 0,

    /**
     * The ticket was cancelled
     */
    Cancelled: -1,

    /**
     * The ticket was successfully handled
     */
    Completed: 1,

    /**
     * The ticket has been responded/seen by an administrator.
     * This state is assumed when an administrator sends a message for the ticket
     */
    Active: 2

});

const AssignmentRuleMode = Object.freeze({
    Enabled: 1,
    Disabled: 0
});

//  Define Search Mode
const SearchMode=  Object.freeze({
    Immediate: 'immediate',
    Explicit: 'explicit'
});

/*
* The request view mode
* */
const RequestTimeFrame = Object.freeze({
    Day:   'day',
    Week: 'week',
    Month: 'month' ,
    Year:  'year'
});

//  Dialogs
const Dialogs = Object.freeze({

    //  Create or Edit Dialog
    CreateOrEdit: 0,

    //  Confirm Dialog
    Confirm: 1,

    //  Details Dialog
    Details: 2
});


const TransactionStatus = Object.freeze({
    Idle: 0,
    Succeeded: 1,
    Failed: -2,
    Invalid: 6,
    Unknown: -9
});


const RequestPaymentStatus = Object.freeze({
    Success: 0,
    Failed: -1,
    Cancelled: -2
});

const PayStatus = Object.freeze({

    /**
     * Unknown payment status
     */
    Unknown: -1,

    /**
     * Payment not initiated
     */
    NotInitiated: -8,

    /**
     * Refund Initiated
     */
    InitiateRefund: 2,

    /**
     * Receive payment started
     */
    InitiateReceive: 6,

    /**
     * Item has been refunded fully
     */
    Refunded: 12,

    /**
     * Item has been refunded partially
     */
    RefundPartial: 24,

    /**
     * Refund failed
     */
    RefundFail: 64,

    /**
     * Indicates a full payment
     */
    PaidFully: 124,


    /**
     * Partial payment
     */
    PaidPartially: 130,


    /**
     *  Indicates a failure in payment
     */
    PaymentFail: 150,

});

const DeliveryConstants = Object.freeze({
    Email: 0,
    SMS: 1
});

/**
 *  Transaction type
 * @type {Object}
 */
const TransactionType = Object.freeze({

    //  Charge or bill customer
    Charge: 0x0001,

    //  Refund money flag
    Refund: 0x0002,

    //  Additional Complementary Flags
    //  Mobile money flag
    MobileMoney: 0x0010,

    //  Cash deposit flag
    Cash: 0x0004,

    //  Card deposit flag
    Card: 0x0008,

    //  Cheque Deposit flag
    Cheque: 0x0020,

    //  Bank Account
    BankAccount: 0x0040,

    // AfricasTalking Wallet
    Wallet: 0x0080,

    //  ----------------------------
    //  Virtual Credit
    //  ----------------------------

    VirtualCredit: 0x0200,

});


//  ********************
//  Enumeration Type Maps
//  ********************

const OwnerPaymentStatusMap = Object.keys(TractorOwnerRequestPaymentStatus);

const RequestStatusMap = Object.keys(RequestStatus);

const RequestTypeMap = Object.keys(TractorRequestType);

const GenderMap = Object.keys(Gender);

const AccountTypeMap = Object.keys(OwnerAccountType);

const ConditionMap = Object.keys(TractorState);

const DeliveryThresholdMap = Object.keys(DeliveryThreshold);

const DeliveryMethodMap = Object.keys(DeliveryConstants);

const TransactionStatusMap = Object.keys(TransactionStatus);

/**
 * Gets the name as string of an enumeration
 * @param enumeration
 * @param value
 * @returns {*}
 */
function getName(enumeration,value) {

    return Object.keys(enumeration).find(k =>{
        return enumeration[k] == value;
    });

}

function resolveValue(enumeration, value) {

    const k = Object.keys(enumeration).find(k =>{
        return k == value;
    });

    if(k){
        return enumeration[k];
    }
}

export {
    Gender,
    SourceEnum,
    OwnerAccountType,
    MediaCategory,
    TractorRequestType,
    TractorRequestBinder,
    TractorState,
    RequestStatusMap,
    MediaStorage,
    RequestStatus,
    UserRoles,
    UserActivity,
    RequestTypeMap,
    GenderMap,
    AccountTypeMap,
    ConditionMap,
    TractorOwnerRequestPaymentStatus,
    OwnerPaymentStatusMap,
    DeliveryThresholdMap,
    TractorSuggestActions,
    TransactionStatus,
    TransactionStatusMap,
    SearchMode,
    DevicePlatforms,
    RequestTimeFrame,
    KnownDeviceCommands,
    DeliveryMethodMap,
    DeliveryConstants,
    TransactionType,
    Dialogs,
    PayStatus,
    DeliveryThreshold,
    UserDepartments,
    TicketPriority,
    TicketStatus,
    ResponseStatus,
    GPSDeviceStatus,
    Permissions,
    VerifyStatus,
    AssignmentRuleMode,
    RequestPaymentStatus,
    PaymentChangeType,
    getName,
    resolveValue
}



// WEBPACK FOOTER //
// ./src/common/Constants.js